import type { ResolvedPayRates } from "@/entities/pay-rate";
import { Button } from "@/shared/components/ui/button";
import { DialogFooter } from "@/shared/components/ui/dialog";
import { ShiftRateRow } from "./ShiftRateRow";
import {
  guardRateView,
  indexResolvedGuards,
  noPayRateForReadOnly,
  notOnPayrollWarning,
  type RateGuard,
} from "./shiftRates";

interface ShiftRatesStepProps {
  guards: RateGuard[];
  resolved: ResolvedPayRates | undefined;
  isLoading: boolean;
  errorMessage: string | null;
  onRetry: () => void;
  customRates: Record<number, number>;
  onCustomRateChange: (guardUserId: number, rate: number | null) => void;
  canManage: boolean;
  onBack: () => void;
  onNext: () => void;
}

export function ShiftRatesStep({
  guards,
  resolved,
  isLoading,
  errorMessage,
  onRetry,
  customRates,
  onCustomRateChange,
  canManage,
  onBack,
  onNext,
}: ShiftRatesStepProps) {
  const resolvedById = indexResolvedGuards(resolved);
  const rows = guards.map((guard) => {
    const resolvedGuard = resolvedById.get(guard.id);
    return { guard, resolvedGuard, view: guardRateView(customRates[guard.id], resolvedGuard) };
  });
  const failed = errorMessage !== null;
  const firstUnrated = resolved && !failed ? rows.find((row) => row.view.rate === null) : undefined;
  const blockedBy = canManage ? firstUnrated?.guard : undefined;
  const nextDisabled = (resolved === undefined && !failed) || blockedBy !== undefined;

  return (
    <div className="space-y-4">
      {failed ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          <span>{errorMessage}</span>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map(({ guard, resolvedGuard, view }) => (
            <ShiftRateRow
              key={guard.id}
              name={guard.name}
              employeeType={guard.employeeType}
              rate={view.rate}
              source={view.source}
              loading={isLoading}
              readOnly={!canManage}
              onRateChange={(rate) =>
                onCustomRateChange(guard.id, rate === resolvedGuard?.rate ? null : rate)
              }
              notices={[
                ...(resolvedGuard?.isOnPayroll === false ? [notOnPayrollWarning(guard.name)] : []),
                ...(!canManage && resolved && view.rate === null
                  ? [noPayRateForReadOnly(guard.name)]
                  : []),
              ]}
            />
          ))}
        </div>
      )}

      {blockedBy && (
        <p role="status" className="text-sm text-destructive">
          Set a pay rate for {blockedBy.name}.
        </p>
      )}

      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="button" onClick={onNext} disabled={nextDisabled}>
          Next
        </Button>
      </DialogFooter>
    </div>
  );
}
